import { Reference } from "@/services/backend/documents/types/reference-number";
import { DocumentStateBase } from "@/services/backend/documents/types/state";
import { Artefact, Document } from "@bitory-gmbh/bsm";
import { EntityId, UUID } from "@/shared/types/entity-id";
import { Address } from "@/services/backend/addresses/types/address";
import { Result } from "@/services/backend/htz/inspection/components/inspection-result";
import { StructuralAssessment } from "@/services/backend/htz/inspection/types/structural-assessment";
import { VisualAssessment } from "@/services/backend/htz/inspection/types/visual-assessment";
import { MicrobialAssessment } from "@/services/backend/htz/inspection/types/microbial-assessment";
import { Photo } from "@/services/backend/htz/inspection/types/photo";
import { Common } from "@/services/backend/documents/types/common";

export interface Report
  extends Document, Artefact, DocumentStateBase, Common, Reference {
  date?: string;
  createdBy: string;
  createdByCompany: Address;
  objectAddress: Address;
  createdFor: Address;
  ahuId: EntityId;
  ahuDataId: EntityId;
  workOrderId: EntityId;
  workOrderPositionId: EntityId;
  laboratories: LabData[];
  inspectionDate?: string;
  inspectionConductedBy: string;
  inspectionConductedByStaff: string;
  lastInspectionDate?: string;
  nextInspectionDate?: string;
  vdi6022Judgement: VDI6022;
  items: Item[];
  microbialAssessments: MicrobialAssessment[];
  photos: Photo[];
  structuralAssessments: StructuralAssessment[];
  visualAssessments: VisualAssessment[];
  ahuImageFileId: string;
}

/* helper for optimisic updates to Report. No validation
 * or Report update logic on purpose. updates through backend. */
export function patchItem(report: Report, item: Item): Report {
  const items = report.items.map((i) => (i.id === item.id ? item : i));
  return {
    ...report,
    items,
  };
}

/* helper for optimisic updates to Report. No validation
 * or Report update logic on purpose, items are updated
 * through backend. */
export function patchMicrobialAssessment(
  report: Report,
  assessment: MicrobialAssessment,
): Report {
  const microbialAssessments = report.microbialAssessments.map((a) => {
    if (a.id === assessment.id) {
      return assessment;
    }
    return a;
  });

  return { ...report, microbialAssessments };
}

/* helper for optimisic updates to Report. No validation
 * or Report update logic on purpose, items are updated
 * through backend. */
export function patchPhotos(report: Report, assessment: Photo): Report {
  const photos = report.photos.map((a) => {
    if (a.id === assessment.id) {
      return assessment;
    }
    return a;
  });

  return { ...report, photos };
}

/* helper for optimisic updates to Report. No validation
 * or Report update logic on purpose, items are updated
 * through backend. */
export function patchStructuralAssessment(
  report: Report,
  assessment: StructuralAssessment,
): Report {
  const structuralAssessments = report.structuralAssessments.map((a) => {
    if (a.id === assessment.id) {
      return assessment;
    }
    return a;
  });

  return { ...report, structuralAssessments };
}

/* helper for optimisic updates to Report. No validation
 * or Report update logic on purpose, items are updated
 * through backend. */
export function patchVisualAssessment(
  report: Report,
  assessment: VisualAssessment,
): Report {
  const visualAssessments = report.visualAssessments.map((a) => {
    if (a.id === assessment.id) {
      return assessment;
    }
    return a;
  });

  return { ...report, visualAssessments };
}

// HTZ_REPORT_NAMESPACE identifies a report as the document a sample was
// taken for. It mirrors the backend namespace of the entity
// (`namespace.For(report.Report{}, "core")`) and must match it exactly: the
// document of a sample cannot be changed once the sample was created.
export const HTZ_REPORT_NAMESPACE = "core.htz.domain.report.Report";

export const VDI6022 = {
  Zero: "",
  Passed: "passed",
  Failed: "failed",
} as const;

export type VDI6022 = (typeof VDI6022)[keyof typeof VDI6022];

export interface Item {
  id: UUID;
  reportId: UUID;
  order: number;
  workOrderPositionId: UUID;
  ahuId: UUID;
  componentId: UUID;
  microbialInspectionResult: Result;
  structuralInspectionResult: Result;
  visualInspectionResult: Result;
  recommendations: string[];
  result: Result;
}

export interface LabData {
  reportId: UUID;
  labId: UUID;
  name: string;
  street: string;
  postalCode: string;
  city: string;
  phone: string;
}
